import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getCatalogById } from '../../services/poetryService';
import { xPoems } from "../../utils/Lang";
import { Poet } from '../../types/poetry';

const GROUPED_THRESHOLD = 50;
const NO_COUNTRY_LABEL = 'Остало';

/** Groups poets by their first letter, preserving catalog sort order within each group. */
function groupByLetter(poets: Poet[]): { label: string; poets: Poet[] }[] {
  const map = new Map<string, Poet[]>();
  for (const poet of poets) {
    const letter = poet.name.trim().charAt(0).toUpperCase();
    if (!map.has(letter)) map.set(letter, []);
    map.get(letter)!.push(poet);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b, 'sr'))
    .map(([label, poets]) => ({ label, poets }));
}

/** Groups poets by country, poets without a country go into a "Остало" bucket. */
function groupByCountry(poets: Poet[]): { label: string; poets: Poet[] }[] {
  const map = new Map<string, Poet[]>();
  for (const poet of poets) {
    const country = poet.country?.trim() || NO_COUNTRY_LABEL;
    if (!map.has(country)) map.set(country, []);
    map.get(country)!.push(poet);
  }
  // Sort country names; keep NO_COUNTRY_LABEL last
  return Array.from(map.entries())
    .sort(([a], [b]) => {
      if (a === NO_COUNTRY_LABEL) return 1;
      if (b === NO_COUNTRY_LABEL) return -1;
      return a.localeCompare(b, 'sr');
    })
    .map(([label, poets]) => ({ label, poets }));
}

const PoetList: React.FC = () => {
  const { catalogId } = useParams<{ catalogId: string }>();

  if (!catalogId) return <Navigate to="/" replace />;

  const catalog = getCatalogById(catalogId);
  if (!catalog) return <Navigate to="/" replace />;

  const useGrouped = catalog.poets.length > GROUPED_THRESHOLD;

  const groups = useGrouped
    ? catalog.group === 'country'
      ? groupByCountry(catalog.poets)
      : groupByLetter(catalog.poets)
    : null;

  const renderPoetItem = (poet: Poet) => (
    <li key={poet.id}>
      <Link to={`/poet/${poet.id}`} className="poet-list__item">
        {poet.name}
        <span className="poet-list__item-count">
          ({poet.poems.length} {xPoems(poet.poems.length)})
        </span>
      </Link>
    </li>
  );

  return (
    <div className="poet-list">
      <h1 className="poet-list__title">{catalog.name}</h1>

      {groups ? (
        groups.map(({ label, poets }) => (
          <section key={label} className="poet-list__letter-section">
            <h2 className="poet-list__letter-heading">{label}</h2>
            <ul className="poet-list__items">
              {poets.map(renderPoetItem)}
            </ul>
          </section>
        ))
      ) : (
        <ul className="poet-list__items">
          {catalog.poets.map(renderPoetItem)}
        </ul>
      )}
    </div>
  );
};

export default PoetList;

